import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import FormInputWrapper from '../form-input-wrapper/FormInputWrapper.jsx';

import { addValidationField, removeValidationField } from '../redux/form/form.actions';

import { DateTime, Settings } from "luxon";
import { uuidv4, filterArrayByProperty } from '../_global/js/form.utils';

/**
 * Configure the Luxon time zone so that all
 * instances of DateTime use the "correct" local time.
 * Timezone from: https://www.iana.org/time-zones
 *
 * @source https://moment.github.io/luxon/#/zones?id=changing-the-default-zone
 */
Settings.defaultZone = "Australia/Queensland";

const FormDate = ({
  groupId,
  inputLabel,
  inputDescriptionText,
  parentElementClass,
  inputName,
  inputRequired,
  sectionLabel,
  inputValidationLabel,
  inputValidationMessage,
  conditionalInputs,
  inputLinked,
  inputMax,
  mostRecentAllowedDate,
  inputTestData,
  outputFormat,
  autoPopulate,
  triggerValidation,
  formStepValidation,
  addValidationField,
  removeValidationField
  }) => {
  const inputId = useRef( uuidv4() );
  const minimumDateISO = DateTime.now().minus({ years: 125 }).toISODate();
  const [ maximumAllowedDate, setMaximumAllowedDate ] = useState( DateTime.now().toISODate() );
  const [ inputValue, setInputValue ] = useState('');
  const [ inputValueFormatted, setInputValueFormatted ] = useState('');
  const [ isValid, setIsValid ] = useState(true);
  const [ requiresFormValidation, setRequiresFormValidation ] = useState(false);
  const validationLabel = inputValidationLabel || inputLabel;
  const [ conditionalQuestions, setConditionalQuestions ] = useState(null);
  const errorMessage = inputValidationMessage || 'This field is required.';

  /**
   * When the component has been mounted
   */
  useEffect(() => {
    // Mark this field as required, as necessary
    if (inputRequired) {
      setRequiresFormValidation(true);
    }

    // Check what the most recent allowed date should be
    if (
      mostRecentAllowedDate !== undefined &&
      mostRecentAllowedDate !== null
    ) {
      // Get the number of years
      const allowedDateYears = parseInt(mostRecentAllowedDate.split('_')[0], 10);

      setMaximumAllowedDate( DateTime.now().minus({ years: allowedDateYears }).toISODate() );
    }

    // Cleanup the component (unmount)
    return () => {
      // Remove this field from the invalid list
      removeValidationField(inputId.current);
    };
  }, []);

  /**
   * When the field validation requirement has changed
   */
  useEffect(() => {
    if (requiresFormValidation) {
      // Add this field to the form validation state
      addValidationField({
        id: inputId.current,
        label: validationLabel,
        sectionLabel: sectionLabel,
        required: inputRequired,
        formStep: formStepValidation
      });
    } else {
      // Remove this field from the form validation state
      removeValidationField(inputId.current);
    }
  }, [requiresFormValidation]);

  /**
   * When the component has been instructed to validate it's input
   */
  useEffect(() => {
    if (triggerValidation !== null) {

      // Check if this input is in the current form step
      if (triggerValidation.formStep === formStepValidation) {
        // Check if the input is valid
        const validationSuccess = inputIsValid(inputValue);

        // Field is required, but failed validation
        if (inputRequired && !validationSuccess) {
          // Display the error message
          setIsValid(false);

          // Mark this field as invalid
          setRequiresFormValidation(true);
        }
      }
    }
  }, [triggerValidation]);

  /**
   * When the component has been instructed to auto-populate content
   */
  useEffect(() => {
    // Populate the field with test data
    if (autoPopulate && inputTestData) {
      setInputValue(inputTestData);
      setIsValid(true);
      setRequiresFormValidation(false);
    }
  }, [autoPopulate]);

  /**
   * When the value of the input has changed
   */
  useEffect(() => {
    let conditionalFieldsArray = [];
    let conditionalFieldsData = null;

    /**
     * Check if the user has entered a value and there are
     * conditional fields that can be displayed
     */
    if (inputIsValid(inputValue) && conditionalInputs) {
      // If conditional inputs have been provided, check for any matches

      if (conditionalInputs[0].responseMatch.indexOf('less-than_') !== -1) {
        // The condition is specific to a date value to check

        // Get the number of years for the comparison
        const yearComparison = parseInt(conditionalInputs[0].responseMatch.split('_')[1], 10);
        const currentDateTime = DateTime.now();
        const selectedDateTime = DateTime.fromISO(inputValue);
        // Get the number of years between the selected date and the current date
        const diffInYears = currentDateTime.diff(selectedDateTime, 'years');

        // Check if the date condition has been met
        if (diffInYears.years < yearComparison) {
          conditionalFieldsArray = [ conditionalInputs[0] ];
        }
      } else {
        // The condition is for an exact match
        conditionalFieldsArray = filterArrayByProperty(conditionalInputs, 'responseMatch', inputValue);
      }

      if (conditionalFieldsArray.length !== 0) {
        // Assign an ID to each input
        conditionalFieldsData = conditionalFieldsArray[0].inputs;

        for (let i = 0, j = conditionalFieldsData.length; i < j; ++i) {
          conditionalFieldsData[i].id = uuidv4();
        }

        setConditionalQuestions(conditionalFieldsData);
      } else {
        setConditionalQuestions(null);
      }
    } else {
      // Reset any conditional questions
      if (conditionalQuestions !== null) {
        setConditionalQuestions(null);
      }
    }
  }, [inputValue]);

  /**
   * Respond to input in the date field
   *
   * @param {Change Event} evt Event that triggered the input to update
   *
   * @returns void
   */
  const onInputChange = (evt) => {
    const fieldValue = evt.target.value;
    let linkedField = null;
    let dateFormatted = null;

    // Check if the input is valid
    const validationSuccess = inputIsValid(fieldValue);

    // Reset the formatted value
    setInputValueFormatted('');

    // Capture the inputted value
    setInputValue(fieldValue);

    // Validate any linked input
    if (inputLinked) {
      linkedField = document.querySelector(`[name=${inputLinked}]`);

      if (linkedField) {
        // Focus the field to trigger the field validation
        linkedField.click();
      }
    }

    // Field is required, but failed validation
    if (inputRequired && !validationSuccess) {
      // Display the error message
      setIsValid(false);

      // Mark this field as invalid
      setRequiresFormValidation(true);
    }

    // Field passed validation
    if (validationSuccess) {
      // Update the validation message
      setIsValid(true);

      // Capture the value in the custom format (if provided)
      if (outputFormat) {
        dateFormatted = DateTime
          .fromISO(fieldValue)
          .toFormat(outputFormat);

        if (dateFormatted !== "Invalid DateTime") {
          setInputValueFormatted(dateFormatted);
        }
      }

      // Remove this field from the invalid list
      setRequiresFormValidation(false);
    }
  };

  /**
   * Validate the field's value
   *
   * @param {String} valueToCheck The value of the input field
   *
   * @returns Boolean
   */
  function inputIsValid(valueToCheck) {
    let isValid = false;
    let linkedDateFieldValue = '';
    let linkedDateComparison = null;
    let allowedDateComparison = null;
    let selectedDateComparison = null;
    let currentDateTime = null;
    let dateDifferenceToCurrent = null;

    if (inputMax) {
      // The linked field must have a valid date or this current field fails validation
      linkedDateFieldValue = document.querySelector(`[name=${inputMax}]`).value;
      linkedDateComparison = DateTime.fromISO(linkedDateFieldValue);
      selectedDateComparison = DateTime.fromISO(valueToCheck);

      if (linkedDateComparison.isValid && selectedDateComparison.isValid) {
        // Compare the dates
         if (selectedDateComparison.startOf('day') <= linkedDateComparison.startOf('day')) {
          // This field must be the same date or earlier than the linked date
          isValid = true;
        }
      }
    } else {
      if (
        mostRecentAllowedDate !== undefined &&
        mostRecentAllowedDate !== null &&
        valueToCheck !== ''
      ) {
        // Check if the provided date is the same or earlier than the allowed date
        // Get the current DateTime Object
        currentDateTime = DateTime.now();
        // Get a DateTime Object of the selected date
        selectedDateComparison = DateTime.fromISO(valueToCheck);
        // Get the number of years for the comparison
        allowedDateComparison = parseInt(mostRecentAllowedDate.split('_')[0], 10);
        // Get the number of years between the selected date and the current date
        dateDifferenceToCurrent = currentDateTime.diff(selectedDateComparison, 'years');

        // Check if the date condition has been met
        if (dateDifferenceToCurrent.years >= allowedDateComparison) {
          isValid = true;
        }
      } else {
        if (valueToCheck !== '') {
          isValid = true;
        }
      }
    }

    return isValid;
  }

  return (
    <div className={parentElementClass}>
      <div>
        <label htmlFor={inputId.current} className="qld__label">{ (inputRequired) ? <abbr title="required">*</abbr> : null } {inputLabel}</label>
        {
          (inputDescriptionText !== undefined && inputDescriptionText !== null) ? <span id={`${inputId.current}-description`} className="qld__hint-text">{inputDescriptionText}</span> : null
        }
        {
          (!isValid) ?
            <p className="sq-form-error">
              {errorMessage}
            </p>
          : null
        }
        <input
          id={inputId.current}
          className={!isValid ? "qld__text-input--error" : null}
          name={inputName}
          type="date"
          value={inputValue}
          onChange={onInputChange}
          onClick={onInputChange}
          required={inputRequired}
          max={maximumAllowedDate}
          min={minimumDateISO}
          autoComplete="off"
          data-groupid={groupId}
          data-validation-label={validationLabel}
          data-section-label={sectionLabel}
          data-value-formatted={inputValueFormatted}
        />
      </div>
      {
        (conditionalQuestions) ?
          <div>
            {
              conditionalQuestions.map( (formInput) => {
                const formInputConfig = { ...formInput };

                /**
                 * If provided, add the section legend (label) to the input configuration.
                 * This is so we can include the section label in the form validation messages and summary.
                 */
                if (sectionLabel) {
                  formInputConfig.sectionLabel = sectionLabel;
                }

                return (
                  <FormInputWrapper
                    key={formInputConfig.id}
                    inputConfiguration={formInputConfig}
                    autoPopulate={autoPopulate}
                    triggerValidation={triggerValidation}
                    formStepValidation={formStepValidation}
                  />
                );
              })
            }
          </div>
        : null
      }
    </div>
  );
};

FormDate.propTypes = {
  "groupId": PropTypes.string,
  "inputLabel": PropTypes.string.isRequired,
  "inputDescriptionText": PropTypes.string,
  "parentElementClass": PropTypes.string,
  "inputName": PropTypes.string.isRequired,
  "inputRequired": PropTypes.bool.isRequired,
  "sectionLabel": PropTypes.string,
  "inputValidationLabel": PropTypes.string,
  "inputValidationMessage": PropTypes.string,
  "conditionalInputs": PropTypes.array,
  "inputLinked": PropTypes.string,
  "inputMax": PropTypes.string,
  "mostRecentAllowedDate": PropTypes.string,
  "inputTestData": PropTypes.string,
  "outputFormat": PropTypes.string,
  "autoPopulate": PropTypes.bool,
  "triggerValidation": PropTypes.object,
  "formStepValidation": PropTypes.number.isRequired,
  "addValidationField":  PropTypes.oneOfType( [PropTypes.object, PropTypes.func] ),
  "removeValidationField":  PropTypes.oneOfType( [PropTypes.string, PropTypes.func] )
};

const mapDispatchToProps = (dispatch) => {
  return {
    "addValidationField": formFieldData => dispatch(addValidationField(formFieldData)),
    "removeValidationField": formFieldData => dispatch(removeValidationField(formFieldData))
  };
};

export default connect(null, mapDispatchToProps)(FormDate);